import React, { useState, useEffect } from "react";
import client from "../../lib/api";
import { telHref, waHref, mailHref, fmtGbp } from "../../mock";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import NotesPanel from "../../components/NotesPanel";
import { CitySelect } from "../../components/CitySelect";
import { useCities } from "../../hooks/useCities";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "../../components/ui/dialog";
import { Phone, MessageCircle, Mail, Briefcase, Flame, Clock, UserPlus, Building2, User, StickyNote, Pencil, MapPin, ArrowDownWideNarrow } from "lucide-react";
import { toast } from "sonner";
import AgentProfile from "./AgentProfile";
import { useLang } from "../../i18n/LangContext";

function Reliability({ value }) {
  const { t } = useLang();
  const color = value >= 88 ? "#1aa860" : value >= 75 ? "#f59e0b" : "#e63946";
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{t("Reliability")}</span>
        <span className="font-medium" style={{ color }}>{value}%</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/8">
        <div className="h-full rounded-full" style={{ width: `${value}%`, background: color, transition: "width 0.8s ease" }} />
      </div>
    </div>
  );
}

const EMPTY = { name: "", agency: "", phone: "", whatsapp: "", email: "", city: "" };
const sanitizePhone = (v) => v.replace(/[^\d+]/g, "");

export default function Agents() {
  const { t } = useLang();
  const [agents, setAgents] = useState([]);
  const [profileId, setProfileId] = useState(() => sessionStorage.getItem("mb_agent_profile") || null);
  const openProfile = (id) => { sessionStorage.setItem("mb_agent_profile", id); setProfileId(id); };
  const closeProfile = () => { sessionStorage.removeItem("mb_agent_profile"); setProfileId(null); };
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [notesAgent, setNotesAgent] = useState(null);
  const [editAgent, setEditAgent] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [savingEdit, setSavingEdit] = useState(false);
  const [cityFilter, setCityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const { cities: cityList, addCity, removeCity } = useCities();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await client.get("/agents");
        setAgents(res.data);
      } catch (e) {
        toast.error("Couldn't load agents");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const set = (k) => (e) => {
    const v = (k === "phone" || k === "whatsapp") ? sanitizePhone(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const setEdit = (k) => (e) => {
    const v = (k === "phone" || k === "whatsapp") ? sanitizePhone(e.target.value) : e.target.value;
    setEditForm((f) => ({ ...f, [k]: v }));
  };

  const openEdit = (a) => {
    setEditForm({ name: a.name || "", agency: a.agency || "", phone: a.phone && a.phone !== "—" ? a.phone : "", whatsapp: a.whatsapp && a.whatsapp !== "—" ? a.whatsapp : "", email: a.email || "", city: a.city || "" });
    setEditAgent(a);
  };
  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim() || !editForm.agency.trim()) { toast.error("Name and agency are required"); return; }
    if (editForm.email.trim() && !editForm.email.includes("@")) { toast.error("Email must contain @"); return; }
    setSavingEdit(true);
    try {
      const res = await client.put(`/agents/${editAgent.id}`, {
        name: editForm.name.trim(), agency: editForm.agency.trim(), phone: editForm.phone.trim() || "—",
        whatsapp: editForm.whatsapp.trim(), email: editForm.email.trim(), city: editForm.city || "",
      });
      setAgents((prev) => prev.map((x) => (x.id === editAgent.id ? { ...x, ...res.data } : x)));
      setEditAgent(null);
      toast.success("Agent updated");
    } catch (err) {
      toast.error("Couldn't update agent", { description: err?.response?.data?.detail });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.agency.trim()) {
      toast.error("Name and agency are required");
      return;
    }
    if (form.email.trim() && !form.email.includes("@")) {
      toast.error("Email must contain @");
      return;
    }
    setSubmitting(true);
    try {
      const res = await client.post("/agents", {
        name: form.name.trim(), agency: form.agency.trim(), phone: form.phone.trim(),
        whatsapp: form.whatsapp.trim(), email: form.email.trim(), city: form.city || "",
      });
      setAgents((prev) => [res.data, ...prev]);
      setForm(EMPTY);
      setOpen(false);
      toast.success("Agent added", { description: `${res.data.name} joined your network.` });
    } catch (err) {
      toast.error("Couldn't add agent", { description: err?.response?.data?.detail });
    } finally {
      setSubmitting(false);
    }
  };

  const cities = cityList || [];
  const shownAgents = cityFilter === "all"
    ? agents
    : agents.filter((a) => a.city === cityFilter || !a.city);
  const sortedAgents = sortBy === "value"
    ? [...shownAgents].sort((a, b) => (b.dealValue || 0) - (a.dealValue || 0))
    : shownAgents;

  if (profileId) return <AgentProfile id={profileId} onBack={closeProfile} onOpenProperty={(pid) => { sessionStorage.setItem("mb_detail", pid); window.dispatchEvent(new CustomEvent("mb:navigate", { detail: "properties" })); }} />;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-white">{t("Agent Network")}</h2>
          <p className="text-xs text-muted-foreground">{shownAgents.length} / {agents.length} {t("Agents").toLowerCase()}{cityFilter !== "all" ? ` · ${cityFilter}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger className="h-10 w-[180px] border-white/12 bg-white/[0.03] text-white" data-testid="agent-city-filter">
              <MapPin className="h-4 w-4 text-muted-foreground" /><SelectValue />
            </SelectTrigger>
            <SelectContent className="glass-strong border-white/10">
              <SelectItem value="all">{t("All cities")}</SelectItem>
              {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-10 w-[170px] border-white/12 bg-white/[0.03] text-white" data-testid="agent-sort">
              <ArrowDownWideNarrow className="h-4 w-4 text-muted-foreground" /><SelectValue />
            </SelectTrigger>
            <SelectContent className="glass-strong border-white/10">
              <SelectItem value="recent">{t("Most recent")}</SelectItem>
              <SelectItem value="value">{t("Deal value")}</SelectItem>
            </SelectContent>
          </Select>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-10 bg-brand-red hover:bg-brand-redDark text-white">
              <UserPlus className="h-4 w-4" /> {t("Add agent")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md glass-strong border-white/10">
            <DialogHeader>
              <DialogTitle className="font-display text-xl text-white">{t("Add agent")}</DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Add an agent to your sourcing network. You can track deals and reliability afterwards.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd} noValidate className="space-y-4 pt-2">
              <div>
                <Label className="text-xs text-muted-foreground">{t("Full name")} *</Label>
                <div className="relative mt-1.5">
                  <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input value={form.name} onChange={set("name")} placeholder="e.g. Karim Benali"
                    className="bg-input/50 pl-10 border-white/10 focus-visible:ring-brand-red" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("Agency")} *</Label>
                <div className="relative mt-1.5">
                  <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input value={form.agency} onChange={set("agency")} placeholder="e.g. Marrakech Prime"
                    className="bg-input/50 pl-10 border-white/10 focus-visible:ring-brand-red" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("City")}</Label>
                <div className="mt-1.5">
                  <CitySelect value={form.city} onChange={(v) => setForm((f) => ({ ...f, city: v }))}
                    cities={cityList} addCity={addCity} removeCity={removeCity}
                    testId="add-agent-city" manageTestId="add-agent-manage-cities" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("Phone (to call)")}</Label>
                <div className="relative mt-1.5">
                  <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input value={form.phone} onChange={set("phone")} inputMode="tel" placeholder="+212 6 00 00 00 00"
                    className="bg-input/50 pl-10 border-white/10 focus-visible:ring-brand-red" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("WhatsApp number")}</Label>
                <div className="relative mt-1.5">
                  <MessageCircle className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input value={form.whatsapp} onChange={set("whatsapp")} inputMode="tel" placeholder="212600000000 (with country code)"
                    className="bg-input/50 pl-10 border-white/10 focus-visible:ring-brand-red" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("Email")}</Label>
                <div className="relative mt-1.5">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input type="email" value={form.email} onChange={set("email")} placeholder="agent@agency.com"
                    className="bg-input/50 pl-10 border-white/10 focus-visible:ring-brand-red" />
                </div>
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}
                  className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">{t("Cancel")}</Button>
                <Button type="submit" disabled={submitting} className="bg-brand-green hover:bg-brand-greenDark text-white">{submitting ? t("Saving…") : t("Add agent")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {sortedAgents.map((a) => (
          <div key={a.id} data-testid={`agent-card-${a.id}`} className="rounded-2xl border border-white/[0.06] bg-card/40 p-5 transition-all hover:-translate-y-1 hover:border-white/15">
            <button onClick={() => openProfile(a.id)} data-testid={`agent-open-${a.id}`} className="mb-2 w-full rounded-lg border border-white/10 bg-white/[0.03] py-1.5 text-xs font-semibold text-white hover:bg-white/[0.08]">{t("View profile · properties · submission link")}</button>
            <div className="flex items-center gap-3">
              <img src={a.avatar} alt={a.name} className="h-12 w-12 rounded-xl object-cover ring-1 ring-white/10" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-sm font-semibold text-white">{a.name}</p>
                <p className="truncate text-xs text-muted-foreground">{a.agency}</p>
                {a.city && <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-brand-green"><MapPin className="h-3 w-3" />{a.city}</p>}
              </div>
              {a.hot > 0 && (
                <span className="flex items-center gap-1 rounded-full bg-brand-red/15 px-2 py-0.5 text-[11px] font-semibold text-brand-red">
                  <Flame className="h-3 w-3" />{a.hot}
                </span>
              )}
            </div>

            <div className="mt-4"><Reliability value={a.reliability} /></div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
              <div className="flex items-center gap-1.5 text-muted-foreground"><Briefcase className="h-3.5 w-3.5" />{a.deals} active deals</div>
              <div className="flex items-center gap-1.5 text-muted-foreground"><Clock className="h-3.5 w-3.5" />{a.lastContact}</div>
            </div>

            <div className="mt-2 flex items-center justify-between text-xs" data-testid="agent-listing-count">
              <span className="flex items-center gap-1.5 text-muted-foreground"><Building2 className="h-3.5 w-3.5" />{a.listingCount || 0} linked {a.listingCount === 1 ? "property" : "properties"}</span>
              {a.dealValue > 0 && <span className="font-medium text-brand-green" data-testid="agent-deal-value">{fmtGbp(a.dealValue)}</span>}
            </div>

            <div className="mt-4 flex gap-2">
              {telHref(a.phone) ? (
                <a href={telHref(a.phone)} data-testid="agent-call" className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/12 bg-white/[0.03] py-2 text-xs font-medium text-white transition-colors hover:bg-white/[0.08]"><Phone className="h-3.5 w-3.5" /> Call</a>
              ) : (
                <span className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/8 bg-white/[0.02] py-2 text-xs text-muted-foreground/50"><Phone className="h-3.5 w-3.5" /> Call</span>
              )}
              {waHref(a.whatsapp || a.phone) ? (
                <a href={waHref(a.whatsapp || a.phone)} target="_blank" rel="noreferrer" data-testid="agent-whatsapp" className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-brand-green/30 bg-brand-green/10 py-2 text-xs font-medium text-brand-green transition-colors hover:bg-brand-green/20"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
              ) : (
                <span className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/8 bg-white/[0.02] py-2 text-xs text-muted-foreground/50"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</span>
              )}
              {mailHref(a.email) ? (
                <a href={mailHref(a.email)} data-testid="agent-email" className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/12 bg-white/[0.03] py-2 text-xs font-medium text-white transition-colors hover:bg-white/[0.08]"><Mail className="h-3.5 w-3.5" /> Email</a>
              ) : (
                <span className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/8 bg-white/[0.02] py-2 text-xs text-muted-foreground/50"><Mail className="h-3.5 w-3.5" /> Email</span>
              )}
            </div>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid="agent-edit-btn"
                className="flex-1 border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
              <Button size="sm" variant="outline" onClick={() => setNotesAgent(a)} data-testid="agent-notes-btn"
                className="flex-1 border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]"><StickyNote className="h-3.5 w-3.5" /> Notes</Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editAgent} onOpenChange={(o) => !o && setEditAgent(null)}>
        <DialogContent className="max-w-md glass-strong border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">{t("Edit agent")}</DialogTitle>
            <DialogDescription className="text-muted-foreground">Update this agent's details. Phone fields accept only + and numbers.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} noValidate className="space-y-4 pt-2">
            <div>
              <Label className="text-xs text-muted-foreground">{t("Full name")} *</Label>
              <div className="relative mt-1.5">
                <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={editForm.name} onChange={setEdit("name")} data-testid="edit-agent-name" className="bg-input/50 pl-10 border-white/10 focus-visible:ring-brand-red" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t("Agency")} *</Label>
              <div className="relative mt-1.5">
                <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={editForm.agency} onChange={setEdit("agency")} data-testid="edit-agent-agency" className="bg-input/50 pl-10 border-white/10 focus-visible:ring-brand-red" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t("City")}</Label>
              <div className="mt-1.5">
                <CitySelect value={editForm.city} onChange={(v) => setEditForm((f) => ({ ...f, city: v }))}
                  cities={cityList} addCity={addCity} removeCity={removeCity}
                  testId="edit-agent-city" manageTestId="edit-agent-manage-cities" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t("Phone (to call)")}</Label>
              <div className="relative mt-1.5">
                <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={editForm.phone} onChange={setEdit("phone")} inputMode="tel" data-testid="edit-agent-phone" placeholder="+212 600000000" className="bg-input/50 pl-10 border-white/10 focus-visible:ring-brand-red" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t("WhatsApp number")}</Label>
              <div className="relative mt-1.5">
                <MessageCircle className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={editForm.whatsapp} onChange={setEdit("whatsapp")} inputMode="tel" data-testid="edit-agent-whatsapp" placeholder="212600000000" className="bg-input/50 pl-10 border-white/10 focus-visible:ring-brand-red" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t("Email")}</Label>
              <div className="relative mt-1.5">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input type="email" value={editForm.email} onChange={setEdit("email")} data-testid="edit-agent-email" placeholder="agent@agency.com" className="bg-input/50 pl-10 border-white/10 focus-visible:ring-brand-red" />
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={() => setEditAgent(null)} className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">{t("Cancel")}</Button>
              <Button type="submit" disabled={savingEdit} data-testid="edit-agent-save" className="bg-brand-green hover:bg-brand-greenDark text-white">{savingEdit ? t("Saving…") : t("Save")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!notesAgent} onOpenChange={(o) => !o && setNotesAgent(null)}>
        <DialogContent className="max-w-lg glass-strong border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">{notesAgent?.name} · Notes</DialogTitle>
            <DialogDescription className="text-muted-foreground">{notesAgent?.agency}</DialogDescription>
          </DialogHeader>
          {notesAgent && <NotesPanel entityType="agent" entityId={notesAgent.id} title="Agent notes" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
